/**
 * ProjectPreviewModal - Project Preview and Duplication Modal
 * Shows detailed project information before duplication
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { Project } from '@fmeastudio/shared';
import { useWizard } from '../../contexts/WizardContext';
import { useProject as useProjectContext } from '../../contexts/ProjectContext';
import { getProjectStats, duplicateProject } from '../../api/projects';
import { useTerminology } from '../../hooks/useTerminology';
import './ProjectPreviewModal.css';

interface ProjectPreviewModalProps {
  project: Project;
  defaultName: string;
  onCancel: () => void;
}

interface ProjectStats {
  componentCount: number;
  failureModeCount: number;
  causeCount: number;
  mitigationCount?: number;
}

export function ProjectPreviewModal({ project, defaultName, onCancel }: ProjectPreviewModalProps) {
  const { cancelWizard } = useWizard();
  const { setActiveProject } = useProjectContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState(defaultName);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTerminology();

  // Fetch project stats
  const { data: stats, isLoading: isLoadingStats } = useQuery<ProjectStats>({
    queryKey: ['project-stats', project.id],
    queryFn: () => getProjectStats(project.id),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Duplicate mutation
  const duplicateMutation = useMutation({
    mutationFn: () => duplicateProject(project.id, newName),
    onSuccess: async (newProject) => {
      // Invalidate projects query to refresh list
      await queryClient.invalidateQueries({ queryKey: ['projects'] });

      // Set as active project
      setActiveProject(newProject);

      // Close wizard
      cancelWizard();

      // Navigate to home (project dashboard)
      navigate('/');
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to duplicate project');
    },
  });

  const handleDuplicate = () => {
    if (!newName.trim()) {
      setError('Project name is required');
      return;
    }
    setError(null);
    duplicateMutation.mutate();
  };

  const formatDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div
      className="project-preview-modal-overlay"
      onClick={onCancel}
      data-testid="project-preview-modal"
    >
      <div className="project-preview-modal" onClick={(e) => e.stopPropagation()}>
        <div className="project-preview-header">
          <h2>Copy: {project.name}</h2>
          <button
            className="project-preview-close"
            onClick={onCancel}
            aria-label="Close preview"
            disabled={duplicateMutation.isPending}
          >
            ×
          </button>
        </div>

        <div className="project-preview-body">
          {/* Project metadata */}
          <section className="project-preview-section">
            <h3>Original Project Details</h3>
            <div className="project-preview-details">
              {project.industry && (
                <div className="project-preview-detail">
                  <span className="detail-label">Industry:</span>
                  <span className="detail-value">{project.industry}</span>
                </div>
              )}
              <div className="project-preview-detail">
                <span className="detail-label">Created:</span>
                <span className="detail-value">{formatDate(project.createdAt)}</span>
              </div>
              {project.description && (
                <div className="project-preview-detail">
                  <span className="detail-label">Description:</span>
                  <p className="detail-value">{project.description}</p>
                </div>
              )}
            </div>
          </section>

          {/* Project statistics */}
          <section className="project-preview-section">
            <h3>Project Contents</h3>
            {isLoadingStats ? (
              <div className="project-preview-loading">
                <div className="spinner-small" />
                <span>Loading project details...</span>
              </div>
            ) : stats ? (
              <div className="project-preview-stats">
                <div className="stat-card">
                  <div className="stat-value">{stats.componentCount}</div>
                  <div className="stat-label">Components</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{stats.failureModeCount}</div>
                  <div className="stat-label">{t.failureModes}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{stats.causeCount}</div>
                  <div className="stat-label">{t.rootCauses}</div>
                </div>
                {stats.mitigationCount !== undefined && (
                  <div className="stat-card">
                    <div className="stat-value">{stats.mitigationCount}</div>
                    <div className="stat-label">{t.mitigations}</div>
                  </div>
                )}
              </div>
            ) : (
              <p className="project-preview-error-text">Could not load project statistics</p>
            )}
          </section>

          {/* What will be copied */}
          <section className="project-preview-section">
            <h3>What will be included?</h3>
            <ul className="project-preview-includes">
              <li>✓ All component hierarchies and relationships</li>
              <li>✓ {t.failureModes} and effects</li>
              <li>✓ {t.rootCauses} and cause-effect links</li>
              <li>✓ {t.rpn} values and risk assessments</li>
              <li>✓ {t.mitigation} actions and recommendations</li>
              <li>✓ User stories and operational flows</li>
            </ul>
          </section>

          {/* New project name input */}
          <section className="project-preview-section">
            <h3>New Project Name</h3>
            <input
              type="text"
              className="project-name-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Enter new project name"
              disabled={duplicateMutation.isPending}
              autoFocus
            />
            {error && <div className="project-preview-error">{error}</div>}
          </section>
        </div>

        <div className="project-preview-footer">
          <button
            className="btn-secondary"
            onClick={onCancel}
            disabled={duplicateMutation.isPending}
          >
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleDuplicate}
            disabled={duplicateMutation.isPending || !newName.trim()}
          >
            {duplicateMutation.isPending ? 'Creating Copy...' : 'Create Copy'}
          </button>
        </div>
      </div>
    </div>
  );
}
